import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useMySavedPaymentMethodsQuery } from '../../../services/stripeService/__generated__/queries.generated';
import { billingAddressLines, SavedCardRow, sortSavedCards } from './paymentMethods';

const Section = styled.section`
  width: 100%;
`;

const Heading = styled.h2`
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #111827;
`;

const Card = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 16px 18px 14px;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  background: #fff;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  column-gap: 20px;
  row-gap: 14px;
  align-items: start;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.p`
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
`;

const CardLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const BrandBadge = styled.span<{ $brand: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 22px;
  min-width: 38px;
  padding: 0 6px;
  border-radius: 3px;
  color: #fff;
  font-family: Georgia, 'Times New Roman', serif;
  font-style: italic;
  font-weight: 700;
  font-size: 13px;
  line-height: 1;
  letter-spacing: -0.02em;
  background: ${({ $brand }) =>
    $brand === 'visa'
      ? '#1a1f71'
      : $brand === 'mastercard'
        ? '#eb001b'
        : $brand === 'amex'
          ? '#2e77bc'
          : '#111827'};
`;

const Number = styled.span`
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.04em;
  color: #111827;
  font-variant-numeric: tabular-nums;
`;

const Address = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: #1f2937;
`;

const Quiet = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: #6b7280;
`;

const Prompt = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
  color: #111827;
`;

const UpdateButton = styled.button`
  margin-top: 12px;
  padding: 6px 14px;
  border: 1px solid #d4d4d8;
  border-radius: 8px;
  background: #fff;
  color: #111827;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;

  &:hover {
    background: #f4f4f5;
    border-color: #a1a1aa;
  }
`;

function brandMark(brand: string): string {
  const key = brand?.toLowerCase();
  if (key === 'visa') return 'Visa';
  if (key === 'mastercard') return 'MC';
  if (key === 'amex') return 'Amex';
  if (key === 'discover') return 'Disc';
  return (brand || 'Card').slice(0, 4);
}

/**
 * Guest profile: the default card on this site, under the phone number.
 * Update opens the manage-cards page.
 */
export const ProfilePaymentMethodsRow: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading } = useMySavedPaymentMethodsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const openPaymentMethods = () => navigate('/payment-methods');
  const cards = (data?.mySavedPaymentMethods ?? []) as SavedCardRow[];
  const card = cards.length > 0 ? sortSavedCards(cards)[0] : null;
  const address = card ? billingAddressLines(card) : [];

  return (
    <Section data-testid="profile-payment-methods">
      <Heading>Payment method</Heading>
      <Card>
        {loading && !data ? (
          <Quiet>Loading card…</Quiet>
        ) : card ? (
          <Columns>
            <div>
              <Eyebrow>Credit card</Eyebrow>
              <CardLine>
                <BrandBadge $brand={card.brand?.toLowerCase()}>{brandMark(card.brand)}</BrandBadge>
                <Number>•••• •••• •••• {card.last4}</Number>
              </CardLine>
              <UpdateButton type="button" onClick={openPaymentMethods}>
                Update
              </UpdateButton>
            </div>
            <div>
              <Eyebrow>Billing address</Eyebrow>
              {address.length > 0 ? (
                address.map(line => <Address key={line}>{line}</Address>)
              ) : (
                <Quiet>Not on file</Quiet>
              )}
            </div>
          </Columns>
        ) : (
          <div>
            <Eyebrow>Credit card</Eyebrow>
            <Prompt>Add a card for checkout</Prompt>
            <UpdateButton type="button" onClick={openPaymentMethods}>
              Update
            </UpdateButton>
          </div>
        )}
      </Card>
    </Section>
  );
};
